import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Modal, Button, StatusBanner } from '../ui';
import { Textarea, Input } from '../ui/FormField';
import { adminBookingAPI } from '../../services/api';
import { BookingListItem } from '../../types/booking';
import { clinicDateTimeToUTC } from '../../utils/timezone';
import { formatDuration } from '../../utils/formatters';
import { apiErrorMessage } from '../../utils/apiError';

// 15 min .. 8 h in 15-min steps (matches the API's 15-480 bounds).
const DURATION_OPTIONS = Array.from({ length: 32 }, (_, i) => (i + 1) * 15);

type DialogType = 'resend' | 'reschedule' | 'remove-buffer' | 'reassign' | 'cancel' | null;

interface RoomOption { id: number; name: string; }

interface Props {
  booking: BookingListItem;
  rooms: RoomOption[];
  onChanged: () => void;
}

const AdminBookingActions: React.FC<Props> = ({ booking, rooms, onChanged }) => {
  const [dialog, setDialog] = useState<DialogType>(null);
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newDuration, setNewDuration] = useState<string>(''); // '' = keep current
  const [newRoomId, setNewRoomId] = useState<string>('');
  const [releaseRoom, setReleaseRoom] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = (d: DialogType) => {
    setReason(''); setInternalNote(''); setNewStart(''); setNewDuration(''); setNewRoomId('');
    setReleaseRoom(true); setError(null); setDialog(d);
  };
  const close = () => { if (!submitting) setDialog(null); };
  const reasonValid = reason.trim().length >= 3;

  const run = async (fn: () => Promise<any>, successMsg: string) => {
    try {
      setSubmitting(true); setError(null);
      await fn();
      toast.success(successMsg);
      setDialog(null);
      onChanged();
    } catch (e: any) {
      setError(apiErrorMessage(e, 'Action failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const isCancelled = booking.status === 'cancelled';
  const hasBuffer = (booking.buffer_minutes || 0) > 0;

  const footer = (onConfirm: () => void, label = 'Confirm', disabled = false) => (
    <div className="flex justify-end gap-3">
      <Button variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
      <Button variant="danger" onClick={onConfirm} isLoading={submitting} disabled={disabled || submitting}>
        {label}
      </Button>
    </div>
  );

  const reasonField = (
    <div>
      <label className="block text-sm font-medium text-clinic-dark mb-1">Reason (required, audit logged)</label>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
        error={reason.length > 0 && !reasonValid} placeholder="Why is this change being made?" />
      {reason.length > 0 && !reasonValid && (
        <p className="text-xs text-clinic-accent mt-1">At least 3 characters.</p>
      )}
    </div>
  );

  return (
    <div className="flex justify-end mt-3">
      {!isCancelled && (
        <Menu as="div" className="relative">
          <MenuButton
            aria-label="Booking actions"
            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-clinic-muted hover:bg-clinic-surface-sunken hover:text-clinic-primary focus:outline-none focus:ring-2 focus:ring-clinic-primary transition-colors"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </MenuButton>
          <MenuItems
            anchor="bottom end"
            className="w-52 mt-1 rounded-lg bg-white shadow-clinic-lg border border-clinic-primary-lighter py-1 z-50 focus:outline-none"
          >
            {([
              { label: 'Reschedule', dialog: 'reschedule' as const, disabled: false },
              { label: 'Remove buffer', dialog: 'remove-buffer' as const, disabled: !hasBuffer },
              { label: 'Reassign room', dialog: 'reassign' as const, disabled: false },
              { label: 'Resend email', dialog: 'resend' as const, disabled: false },
            ]).map((a) => (
              <MenuItem key={a.dialog} disabled={a.disabled}>
                {(bag: any) => (
                  <button
                    onClick={() => open(a.dialog)}
                    disabled={a.disabled}
                    className={`w-full text-left px-4 py-2.5 text-sm text-clinic-dark disabled:opacity-40 disabled:cursor-not-allowed ${(bag.focus || bag.active) && !a.disabled ? 'bg-clinic-surface-sunken' : ''}`}
                  >
                    {a.label}
                  </button>
                )}
              </MenuItem>
            ))}
            <div className="my-1 border-t border-clinic-primary-lighter" />
            <MenuItem>
              {(bag: any) => (
                <button
                  onClick={() => open('cancel')}
                  className={`w-full text-left px-4 py-2.5 text-sm font-medium text-clinic-accent ${bag.focus || bag.active ? 'bg-clinic-accent/10' : ''}`}
                >
                  Force cancel
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>
      )}

      {/* Resend confirmation */}
      <Modal isOpen={dialog === 'resend'} onClose={close} title="Resend confirmation email" size="sm"
        footer={footer(() => run(() => adminBookingAPI.resendConfirmation(booking.id, reason.trim()), 'Confirmation email queued'), 'Resend', !reasonValid)}>
        <div className="space-y-4">
          {error && <StatusBanner variant="error" title="Failed" message={error} />}
          {reasonField}
        </div>
      </Modal>

      {/* Reschedule */}
      <Modal isOpen={dialog === 'reschedule'} onClose={close} title="Reschedule booking" size="sm"
        footer={footer(
          () => run(() => adminBookingAPI.reschedule(
            booking.id, clinicDateTimeToUTC(newStart), reason.trim(),
            newDuration ? Number(newDuration) : undefined,
          ), 'Booking rescheduled'),
          'Reschedule', !reasonValid || !newStart)}>
        <div className="space-y-4">
          {error && <StatusBanner variant="error" title="Failed" message={error} />}
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">New start time</label>
            <Input type="datetime-local" step={900} value={newStart} onChange={(e) => setNewStart(e.target.value)} />
            <p className="text-xs text-clinic-muted mt-1">Must fall on a 15-minute boundary.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Duration</label>
            <select
              value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
            >
              <option value="">Keep current ({formatDuration(booking.duration_minutes)})</option>
              {DURATION_OPTIONS.map(m => (
                <option key={m} value={m}>{formatDuration(m)}</option>
              ))}
            </select>
            <p className="text-xs text-clinic-muted mt-1">
              Shortening a booking releases the freed time back to the clinician's availability.
            </p>
          </div>
          {reasonField}
        </div>
      </Modal>

      {/* Remove buffer */}
      <Modal isOpen={dialog === 'remove-buffer'} onClose={close} title="Remove buffer" size="sm"
        footer={footer(() => run(() => adminBookingAPI.removeBuffer(booking.id, reason.trim()), 'Buffer removed'), 'Remove', !reasonValid)}>
        <div className="space-y-4">
          {error && <StatusBanner variant="error" title="Failed" message={error} />}
          <p className="text-sm text-clinic-muted">Removes the {booking.buffer_minutes} min buffer and frees those blocks.</p>
          {reasonField}
        </div>
      </Modal>

      {/* Reassign room */}
      <Modal isOpen={dialog === 'reassign'} onClose={close} title="Reassign room" size="sm"
        footer={footer(
          () => run(() => adminBookingAPI.reassignRoom(booking.id, Number(newRoomId), reason.trim()), 'Room reassigned'),
          'Reassign', !reasonValid || !newRoomId)}>
        <div className="space-y-4">
          {error && <StatusBanner variant="error" title="Failed" message={error} />}
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">New room</label>
            <select value={newRoomId} onChange={(e) => setNewRoomId(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-clinic-primary-light rounded-input text-clinic-dark focus:outline-none focus:ring-2 focus:ring-clinic-primary/20">
              <option value="">Select a room…</option>
              {rooms.filter((r) => r.name !== booking.room_name).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          {reasonField}
        </div>
      </Modal>

      {/* Force cancel */}
      <Modal isOpen={dialog === 'cancel'} onClose={close} title="Force cancel booking" size="sm"
        footer={footer(
          () => run(() => adminBookingAPI.forceCancel(booking.id, {
            reason: reason.trim(), internal_note: internalNote.trim() || undefined, release_room: releaseRoom,
          }), 'Booking cancelled'),
          'Cancel booking', !reasonValid)}>
        <div className="space-y-4">
          {error && <StatusBanner variant="error" title="Failed" message={error} />}
          <label className="flex items-center gap-2 text-sm text-clinic-dark">
            <input type="checkbox" checked={releaseRoom} onChange={(e) => setReleaseRoom(e.target.checked)} className="accent-clinic-primary" />
            Release the room (clinician not billed). Uncheck to keep it billed.
          </label>
          {reasonField}
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Internal note (optional, admin only)</label>
            <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} placeholder="Not shown to the patient." />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminBookingActions;
